import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import Form from '../ui/forms';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import { generateGames } from '../../features/game/gameSlice';
import { getEventPlayerOpponentsByRound } from '../../features/event/eventSlice';
import { getAllPatterns } from '../../features/green/greenSlice';

const generateTypes = [
  {
    value: 'strength-vs-strength',
    label: 'Strength VS Strength',
    description: 'Generate games in a strength-vs-strength order. 1vs2, 3vs4, 5vs6 etc.'
  },
  {
    value: 'round-robin',
    label: 'Round Robin',
    description: 'Generate games in a round robin format.'
  }
];

const schema = yup.object().shape({
  generateType: yup.string().required('Generation type is required'),
  gameNamePrefix: yup.string().max(20, 'Prefix too long'),
  round: yup.number().when('generateType', {
    is: (val) => val === 'strength-vs-strength',
    then: (schemaRef) =>
      schemaRef.required('Round is required').min(1, 'Round must be at least 1'),
    otherwise: (schemaRef) => schemaRef.notRequired()
  }),
  usePattern: yup.boolean(),
  patternId: yup.string().nullable()
});

const GenerateSectionGames = ({ event, section, sectionGames = [], open, onClose }) => {
  const dispatch = useDispatch();
  const generatingGames = useSelector((state) => state.game.generatingGames);
  const patterns = useSelector((state) => state.green.patterns);

  const totalRounds = Number(event?.gameFormat?.rounds || 0);
  const hasRounds = totalRounds > 1;
  const hasLog =
    (section?.baseLog && section.baseLog.length > 0) ||
    (section?.initialDraw && section.initialDraw.length > 0);
  const hasGreenSetups = Array.isArray(event?.greens) && event.greens.length > 0;
  const canGenerate = hasRounds && hasLog && hasGreenSetups;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      generateType: '',
      gameNamePrefix: '',
      round: 1,
      usePattern: false,
      patternId: ''
    }
  });

  const generateType = watch('generateType');
  const usePattern = watch('usePattern');
  const selectedRound = Number(watch('round') || 1);

  const generatedRounds = useMemo(() => {
    const rounds = new Set();

    sectionGames.forEach((game) => {
      const roundNumber = Number(game?.round || 0);
      if (Number.isFinite(roundNumber) && roundNumber > 0) {
        rounds.add(roundNumber);
      }
    });

    return Array.from(rounds).sort((a, b) => a - b);
  }, [sectionGames]);

  const generatedRoundsSet = useMemo(() => new Set(generatedRounds), [generatedRounds]);

  const availableRounds = useMemo(() => {
    return [...Array(totalRounds)]
      .map((_, i) => i + 1)
      .filter((round) => !generatedRoundsSet.has(round));
  }, [totalRounds, generatedRoundsSet]);

  const nextRoundToGenerate = availableRounds[0] || 1;
  const selectedRoundAlreadyGenerated = generatedRoundsSet.has(selectedRound);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (generateType === 'strength-vs-strength') {
      setValue('round', nextRoundToGenerate, { shouldValidate: true });
    }
  }, [open, generateType, nextRoundToGenerate, setValue]);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Patterns are only fetched by the Green Patterns page. Fetch them when
    // this dialog opens so freshly saved patterns show up here without
    // having to visit that page first.
    dispatch(getAllPatterns());
  }, [open, dispatch]);

  const onSubmit = async (data) => {
    if (data.generateType === 'strength-vs-strength' && generatedRoundsSet.has(Number(data.round))) {
      return;
    }
    await dispatch(generateGames({ ...data, eventId: event.id, sectionId: section.id }));
    await dispatch(getEventPlayerOpponentsByRound({ eventId: event.id }));
    onClose();
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Generate Games</DialogTitle>
      <DialogContent>
        {!canGenerate ? (
          <Box display={'flex'} flexDirection={'column'} gap={1}>
            <Typography color="error">Cannot generate games. The following are required:</Typography>
            {!hasRounds && (
              <Typography variant="body2">- Game format must have more than 1 round</Typography>
            )}
            {!hasLog && (
              <Typography variant="body2">- Section must have an initial draw or log</Typography>
            )}
            {!hasGreenSetups && (
              <Typography variant="body2">
                - Event must have greens set up (configure greens &amp; rinks in the Green Organiser —
                this is separate from the optional Green Pattern)
              </Typography>
            )}
          </Box>
        ) : (
          <Box display={'flex'} flexDirection={'column'} gap={2}>
            <Typography>
              Games will be generated based on the initial draw or the log, the round, and assign a
              green.
            </Typography>
            <Typography>Click on the button below to generate games for this section.</Typography>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Select
                label="Generation Type"
                name="generateType"
                control={control}
                error={errors.generateType}
                disabled={generatingGames}>
                {generateTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1">{type.label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>

              <Input
                label="Game Name Prefix"
                error={errors.gameNamePrefix}
                disabled={generatingGames}
                {...register('gameNamePrefix')}
              />

              {generateType === 'strength-vs-strength' && totalRounds > 0 && (
                <>
                  <Select
                    name="round"
                    label={'Round'}
                    control={control}
                    error={errors.round}
                    disabled={generatingGames || availableRounds.length === 0}>
                    {[...Array(totalRounds)].map((_, i) => {
                      const roundNumber = i + 1;
                      const alreadyGenerated = generatedRoundsSet.has(roundNumber);

                      return (
                        <MenuItem key={i} value={roundNumber} disabled={alreadyGenerated}>
                          Round {roundNumber}
                          {alreadyGenerated ? ' (already generated)' : ''}
                        </MenuItem>
                      );
                    })}
                  </Select>

                  {availableRounds.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" mt={-1}>
                      Next available round selected automatically: Round {nextRoundToGenerate}
                    </Typography>
                  ) : (
                    <Typography color="warning.main" variant="body2" mt={-1}>
                      All rounds already have generated games for this section.
                    </Typography>
                  )}
                </>
              )}

              {generateType === 'strength-vs-strength' && (
                <Box mt={1}>
                  <Controller
                    name="usePattern"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox {...field} checked={field.value} disabled={generatingGames} />
                        }
                        label="Use Green Pattern"
                      />
                    )}
                  />
                </Box>
              )}

              {(generateType === 'round-robin' ||
                (generateType === 'strength-vs-strength' && usePattern)) && (
                <Select
                  name="patternId"
                  label={'Green Pattern'}
                  control={control}
                  error={errors.patternId}
                  disabled={generatingGames}>
                  <MenuItem value="">None</MenuItem>
                  {patterns &&
                    patterns.map((pattern) => (
                      <MenuItem key={pattern.id} value={pattern.id}>
                        <Box>
                          <Typography variant="body1">{pattern.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {pattern.sectionsCount} sections, {pattern.roundsCount} rounds,{' '}
                            {pattern.gamesPerRound} games per round, {pattern.greensCount} greens,{' '}
                            {pattern.rinksCount} rinks
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              )}

              <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="submit"
                  disabled={
                    generatingGames ||
                    (generateType === 'strength-vs-strength' &&
                      (availableRounds.length === 0 || selectedRoundAlreadyGenerated))
                  }>
                  Generate Games
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={generatingGames}>
                  Cancel
                </Button>
              </Box>
            </Form>
          </Box>
        )}
      </DialogContent>

      {!canGenerate && (
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GenerateSectionGames;
